import { MARK } from '../constants';

/**
 * A agenda DESENHADA, e não fotografada.
 *
 * Fotografar a tela levaria junto o que é da tela: a célula da semana rola por
 * dentro e cortaria o dia cheio, os botões sairiam na imagem, e o tamanho
 * dependeria do monitor de quem exporta. Desenhando, a imagem tem sempre a
 * mesma cara, mostra todos os eventos e cresce para caber o dia mais cheio.
 *
 * Sem biblioteca: é texto e retângulo, e o canvas do navegador faz os dois.
 */

const ESCALA = 2;            // nitidez no zoom do WhatsApp
const MARGEM = 32;
const COLUNA = 232;
const FONTE = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const COR = {
  fundo: '#ffffff',
  texto: '#0f172a',
  suave: '#64748b',
  linha: '#e2e8f0',
  cabecalho: '#f8fafc',
  hoje: '#eff6ff',
  hojeTexto: '#2563eb',
  etapa: { fundo: '#f1f5f9', borda: '#94a3b8', texto: '#334155' },
  prazo: { fundo: '#fffbeb', borda: '#f59e0b', texto: '#78350f' },
  vencido: { fundo: '#fff1f2', borda: '#e11d48', texto: '#881337' },
};

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

const fonte = (px, peso = 400) => `${peso} ${px}px ${FONTE}`;

/** Quebra o texto em linhas que caibam em `largura`, até `max` linhas. */
const quebrar = (ctx, texto, largura, max = 3) => {
  const palavras = String(texto || '').split(/\s+/).filter(Boolean);
  const linhas = [];
  let atual = '';
  palavras.forEach(p => {
    const tentativa = atual ? `${atual} ${p}` : p;
    if (ctx.measureText(tentativa).width <= largura || !atual) atual = tentativa;
    else { linhas.push(atual); atual = p; }
  });
  if (atual) linhas.push(atual);
  if (linhas.length > max) {
    const cortadas = linhas.slice(0, max);
    let ultima = cortadas[max - 1];
    while (ultima.length > 1 && ctx.measureText(`${ultima}…`).width > largura) ultima = ultima.slice(0, -1);
    cortadas[max - 1] = `${ultima}…`;
    return cortadas;
  }
  // Uma palavra só maior que a coluna (nome sem espaço) também se corta.
  return linhas.map(l => {
    let s = l;
    if (ctx.measureText(s).width <= largura) return s;
    while (s.length > 1 && ctx.measureText(`${s}…`).width > largura) s = s.slice(0, -1);
    return `${s}…`;
  });
};

/** Uma tela de medir: o canvas perde o contexto quando muda de tamanho. */
const medidor = () => document.createElement('canvas').getContext('2d');

const novaTela = (largura, altura) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(largura * ESCALA);
  canvas.height = Math.ceil(altura * ESCALA);
  const ctx = canvas.getContext('2d');
  ctx.scale(ESCALA, ESCALA);
  ctx.fillStyle = COR.fundo;
  ctx.fillRect(0, 0, largura, altura);
  ctx.textBaseline = 'top';
  return { canvas, ctx };
};

const cabecalho = (ctx, { laboratorio, titulo, subtitulo }, largura) => {
  let y = MARGEM;
  if (laboratorio) {
    ctx.font = fonte(14, 600);
    ctx.fillStyle = COR.suave;
    ctx.fillText(laboratorio.toUpperCase(), MARGEM, y);
    y += 22;
  }
  ctx.font = fonte(24, 700);
  ctx.fillStyle = COR.texto;
  ctx.fillText(titulo, MARGEM, y);
  y += 34;
  if (subtitulo) {
    ctx.font = fonte(13);
    ctx.fillStyle = COR.suave;
    ctx.fillText(subtitulo, MARGEM, y);
    y += 22;
  }
  ctx.fillStyle = COR.linha;
  ctx.fillRect(MARGEM, y + 6, largura - 2 * MARGEM, 1);
  return y + 20;
};

const ALTURA_DO_CABECALHO = (c) => MARGEM + (c.laboratorio ? 22 : 0) + 34 + (c.subtitulo ? 22 : 0) + 20;

const geradoEm = () => `Gerado em ${new Date().toLocaleDateString('pt-BR')}`;

// ---------------------------------------------------------------------------
// AGENDA
// ---------------------------------------------------------------------------

const LARGURA_DO_TEXTO = COLUNA - 8 - 16;

/**
 * As linhas de um evento, já quebradas. É daqui que sai a altura do bloco,
 * então medir e desenhar usam a mesma lista — não há como divergirem.
 */
const linhasDoEvento = (ctx, ev) => {
  const out = [];
  const pega = (texto, px, peso, cor, max) => {
    if (!texto) return;
    ctx.font = fonte(px, peso);
    quebrar(ctx, texto, LARGURA_DO_TEXTO, max).forEach(t => out.push({ t, px, peso, cor }));
  };
  pega(`${ev.feito ? `${MARK.OK} ` : ''}${ev.vencido ? `${MARK.WARN} ` : ''}${ev.rotulo}`, 13, 700, null, 1);
  pega(ev.cliente, 12, 500, COR.texto, 2);
  pega(ev.lote, 11, 400, COR.suave, 2);
  pega(ev.detalhe, 12, 600, null, 3);
  return out;
};

const alturaDasLinhas = (linhas) => linhas.reduce((h, l) => h + Math.round(l.px * 1.35), 0);

/**
 * `semanas`: lista de semanas, cada uma com sete datas ISO (null = fora do
 * período). `eventosPorDia[iso]`: eventos já prontos para desenhar —
 * { rotulo, cliente, lote, detalhe, feito, prazo, vencido }.
 */
export const desenharAgenda = ({ semanas, eventosPorDia, hoje, laboratorio, titulo }) => {
  const m = medidor();
  const largura = 2 * MARGEM + 7 * COLUNA;
  const topo = { laboratorio, titulo, subtitulo: geradoEm() };

  // Primeiro medir: a altura de cada semana é a do dia mais cheio.
  const blocos = semanas.map(dias => dias.map(iso => (iso ? (eventosPorDia[iso] || []) : [])
    .map(ev => { const linhas = linhasDoEvento(m, ev); return { ev, linhas, h: alturaDasLinhas(linhas) + 12 }; })));
  const alturasDasSemanas = blocos.map(dias => Math.max(56,
    ...dias.map(evs => evs.reduce((h, b) => h + b.h + 6, 6))));
  const CABECA_DO_DIA = 30;
  const altura = ALTURA_DO_CABECALHO(topo)
    + alturasDasSemanas.reduce((h, a) => h + CABECA_DO_DIA + a + 16, 0) + MARGEM;

  const { canvas, ctx } = novaTela(largura, altura);
  let y = cabecalho(ctx, topo, largura);

  semanas.forEach((dias, w) => {
    const alturaDaSemana = alturasDasSemanas[w];
    dias.forEach((iso, i) => {
      const x = MARGEM + i * COLUNA;
      const ehHoje = iso && iso === hoje;
      // Régua do dia.
      ctx.fillStyle = ehHoje ? COR.hoje : COR.cabecalho;
      ctx.fillRect(x, y, COLUNA - 1, CABECA_DO_DIA);
      if (iso) {
        ctx.font = fonte(12, 700);
        ctx.fillStyle = ehHoje ? COR.hojeTexto : COR.suave;
        ctx.fillText(`${DIAS[i].toUpperCase()}  ${iso.slice(8, 10)}/${iso.slice(5, 7)}`, x + 8, y + 9);
      }
      // A coluna.
      ctx.fillStyle = ehHoje ? COR.hoje : COR.fundo;
      ctx.fillRect(x, y + CABECA_DO_DIA, COLUNA - 1, alturaDaSemana);
      ctx.fillStyle = COR.linha;
      ctx.fillRect(x + COLUNA - 1, y, 1, CABECA_DO_DIA + alturaDaSemana);

      let yy = y + CABECA_DO_DIA + 6;
      blocos[w][i].forEach(({ ev, linhas, h }) => {
        const tom = ev.vencido ? COR.vencido : ev.prazo ? COR.prazo : COR.etapa;
        ctx.fillStyle = tom.fundo;
        ctx.fillRect(x + 4, yy, COLUNA - 9, h);
        ctx.fillStyle = tom.borda;
        ctx.fillRect(x + 4, yy, 3, h);
        let ly = yy + 6;
        linhas.forEach(l => {
          ctx.font = fonte(l.px, l.peso);
          ctx.fillStyle = l.cor || tom.texto;
          ctx.fillText(l.t, x + 14, ly);
          ly += Math.round(l.px * 1.35);
        });
        yy += h + 6;
      });
      if (iso && blocos[w][i].length === 0) {
        ctx.font = fonte(12);
        ctx.fillStyle = '#cbd5e1';
        ctx.fillText('—', x + 12, y + CABECA_DO_DIA + 12);
      }
    });
    ctx.fillStyle = COR.linha;
    ctx.fillRect(MARGEM, y + CABECA_DO_DIA + alturaDaSemana, 7 * COLUNA, 1);
    y += CABECA_DO_DIA + alturaDaSemana + 16;
  });

  return canvas;
};

// ---------------------------------------------------------------------------

const comoArquivo = (canvas, nome) => new Promise((resolve) => {
  canvas.toBlob(blob => resolve(blob ? new File([blob], nome, { type: 'image/png' }) : null), 'image/png');
});

/** No celular o compartilhamento nativo leva direto ao WhatsApp. */
export const podeCompartilhar = () => {
  try {
    const teste = new File([''], 'x.png', { type: 'image/png' });
    return !!(navigator.canShare && navigator.canShare({ files: [teste] }));
  } catch {
    return false;
  }
};

export const compartilhar = async (canvas, nome, titulo) => {
  const arquivo = await comoArquivo(canvas, nome);
  if (!arquivo) return false;
  try {
    await navigator.share({ files: [arquivo], title: titulo });
    return true;
  } catch (e) {
    // Cancelar o compartilhamento não é erro de ninguém.
    return e?.name === 'AbortError';
  }
};

export const baixar = async (canvas, nome) => {
  const arquivo = await comoArquivo(canvas, nome);
  if (!arquivo) return;
  const url = URL.createObjectURL(arquivo);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
